import { useMemo } from "react"
import { Box, MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import type { MRT_ColumnDef } from "material-react-table"
import { makeStyles } from "tss-react/mui"

import { MeetingCardMenu } from "../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { ApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useColumnFactory } from "../../../../../core/api/useTableColumns"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import type { StandardChipVariantType } from "../../../../../shared/components/badges/StandardChip"
import { ProductCardTitle } from "../../../../../shared/components/createOrder/components/shop/product/ProductCardTitle"
import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import { SearchableText } from "../../../../../shared/components/search/Searchable"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { defaultDisplayColumnDefOptions } from "../../../../../shared/constants/table/config"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { getHostOrganizer } from "../../home/components/meetigs/helpers"
import type { ServiceCenterBookingOrganizer } from "../../home/serviceCenterHomeTypes"

import { formatTimeWindow, toSearchRegex } from "../meetingRoomsLib"
import { MeetingRoomStatus } from "../meetingRoomsTypes"
import type { MeetingRoomRow, MeetingRoomTablePagination, MeetingRoomTableRow } from "../meetingRoomsTypes"
import { LIVE_DOT, toMenuMeeting } from "./helpers"
import { MeetingRoomBookingTags } from "./MeetingRoomBookingTags"
import { MeetingRoomTimeCell } from "./MeetingRoomTimeCell"

// Booking actions need the order details; when they fail to load, the menu keeps only the room items.
const renderNoBookingActions = () => null

const STATUS_COLUMN_SIZE = 190

const SKELETON_ROWS = 10

const STATUS_CHIP_VARIANTS: Record<MeetingRoomStatus, StandardChipVariantType> = {
    [MeetingRoomStatus.BOOKED]: "info",
    [MeetingRoomStatus.AVAILABLE]: "success",
    [MeetingRoomStatus.NOT_CHECKED_IN]: "info",
    [MeetingRoomStatus.OUT_OF_SERVICE]: "grey",
}

// Fits inside the 1rem, 1px frame the controller draws around the table on paper.
const PRINT_SHEET_INNER_RADIUS = "calc(1rem - 1px)"

type StyleProps = Readonly<{
    pageStart: number
    pageEnd: number
}>

const useStyles = makeStyles<StyleProps>()((theme, { pageStart, pageEnd }) => ({
    tableContainer: {
        padding: 0,
        borderRadius: 0,
        width: 0,
        minWidth: "100%",
        overflowX: "auto",
        "& table": {
            minWidth: "60rem",
        },
        // MRT caps the empty-state text width by the paper width measured on first render (360px before mount).
        "& tbody td[colspan] > .MuiTypography-root": {
            maxWidth: "none",
        },
        // Same height for skeleton, empty and filled rows so the card does not jump while data loads.
        "& tbody td": {
            height: "5.5rem",
        },
        "@media print": {
            overflow: "visible",
            "& table": {
                minWidth: 0,
                width: "100%",
            },
            "& .MuiIconButton-root": {
                display: "none",
            },
            // The row actions column is the last one and is hidden on paper, so the visible edge is the one before it.
            "& th:first-of-type, & td:first-of-type": {
                paddingLeft: "1.5rem",
            },
            "& th:nth-last-of-type(2), & td:nth-last-of-type(2)": {
                paddingRight: "1.5rem",
            },
            "& tr": {
                backgroundColor: "transparent",
            },
            "& thead th": {
                backgroundColor: theme.palette.tailwind.slate[50],
                borderBottom: "none",
            },
            "& thead th:first-of-type": {
                borderTopLeftRadius: PRINT_SHEET_INNER_RADIUS,
            },
            "& thead th:nth-last-of-type(2)": {
                borderTopRightRadius: PRINT_SHEET_INNER_RADIUS,
            },
            "& tbody tr:last-of-type td:first-of-type": {
                borderBottomLeftRadius: PRINT_SHEET_INNER_RADIUS,
            },
            "& tbody tr:last-of-type td:nth-last-of-type(2)": {
                borderBottomRightRadius: PRINT_SHEET_INNER_RADIUS,
            },
            // MRT sizes columns for the screen; on paper they have to shrink to the page width.
            "& th, & td": {
                minWidth: "0 !important",
                maxWidth: "none !important",
                width: "auto !important",
            },
            "& thead": {
                display: "table-header-group",
            },
            "& tbody tr": {
                breakInside: "avoid",
            },
            // A row cut off by a page break must not draw a line through the rounded corner of the frame.
            "& tbody td": {
                height: "auto",
                borderBottom: "none",
                borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
            },
        },
    },
    printHidden: HIDDEN_IN_PRINT,
    // A column's own head cell class replaces the table one, which is what removes the default cell border.
    actionsHeadCell: {
        border: "none",
        ...HIDDEN_IN_PRINT,
    },
    // The title clamps to the column width measured on screen; paper shows the full name instead.
    printOnlyTitle: {
        display: "none",
        fontWeight: 600,
        "@media print": {
            display: "block",
        },
    },
    // MRT renders one skeleton row per page size (100 via URL pagination); keep the loading card short.
    tableContainerLoading: {
        [`& tbody tr:nth-of-type(n+${SKELETON_ROWS + 1})`]: {
            display: "none",
        },
    },
    // Every row stays rendered so that print still lists all rooms; the screen shows only the current page.
    tableContainerPaged: {
        "@media screen": {
            [`& tbody tr:nth-of-type(-n+${pageStart})`]: {
                display: "none",
            },
            [`& tbody tr:nth-of-type(n+${pageEnd + 1})`]: {
                display: "none",
            },
        },
    },
    // MRT has no slot for a custom empty text, so its empty row is replaced by the one below the table.
    tableContainerNoMatch: {
        "& tbody": {
            display: "none",
        },
    },
    noMatch: {
        padding: "3.5rem 1.5rem",
        textAlign: "center",
    },
    paper: {
        boxShadow: "none",
        border: "none",
        "@media print": {
            background: "none",
            "& .MuiLinearProgress-root": {
                display: "none",
            },
        },
    },
    roomName: {
        fontWeight: 700,
    },
    row: {
        cursor: "pointer",
    },
    liveDot: LIVE_DOT,
}))

type Props = Readonly<{
    rows: ReadonlyArray<MeetingRoomTableRow>
    pagination: MeetingRoomTablePagination
    hasRooms: boolean
    isInitialLoading: boolean
    isFetching: boolean
    isError: boolean
    onRowOpen: (row: MeetingRoomTableRow) => void
    onHostOpen: (host: ServiceCenterBookingOrganizer) => void
    onBook?: (room: MeetingRoomRow) => void
    search: string
    hourLabel: string
    hourRangeLabel: string
    dayEndMillis: number
}>

export function MeetingRoomsTable({
    rows,
    pagination: { pageIndex, pageSize },
    hasRooms,
    isInitialLoading,
    isFetching,
    isError,
    onRowOpen,
    onHostOpen,
    onBook,
    search,
    hourLabel,
    hourRangeLabel,
    dayEndMillis,
}: Props) {
    const pageStart = pageIndex * pageSize
    const { classes, cx } = useStyles({ pageStart, pageEnd: pageStart + pageSize })
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const searchRegex = useMemo(() => toSearchRegex(search), [search])

    const tableData = useMemo(() => [...rows], [rows])
    const columnFactory = useColumnFactory<MeetingRoomTableRow>()

    const columns = useMemo<MRT_ColumnDef<MeetingRoomTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                id: "room",
                header: l10n.getString("service-center-meeting-rooms-table-room-header"),
                accessorFn: (row) => row.room.name,
                Cell: ({ row, renderedCellValue }) => {
                    const { floorName, seats } = row.original.room

                    return (
                        <StandardTwoLinesCell
                            title={
                                <Typography component="span" variant="subtitle2" className={classes.roomName}>
                                    {renderedCellValue}
                                </Typography>
                            }
                            subtitle1={
                                seats !== undefined ? (
                                    <LocalizedStrict
                                        id="service-center-meeting-rooms-table-room-seats"
                                        vars={{ floor: floorName, seats }}
                                    >
                                        <span>
                                            {floorName} · {seats}
                                        </span>
                                    </LocalizedStrict>
                                ) : (
                                    floorName
                                )
                            }
                        />
                    )
                },
            }),
            columnFactory.nameColumn({
                id: "booking",
                header: l10n.getString("service-center-meeting-rooms-table-booking-header"),
                accessorFn: (row) => row.booking?.meetingName ?? "",
                Cell: ({ row }) => {
                    const { booking, room } = row.original
                    if (!booking) {
                        return (
                            <LocalizedStrict id="service-center-meeting-rooms-table-no-booking">
                                <Typography variant="body2" color="text.secondary">
                                    No booking
                                </Typography>
                            </LocalizedStrict>
                        )
                    }

                    const organizers = [...booking.organizers]
                    const host = getHostOrganizer(organizers)

                    return (
                        <Stack spacing=".5rem" alignItems="flex-start">
                            <Box className={classes.printHidden}>
                                <ProductCardTitle
                                    name={booking.meetingName ?? ""}
                                    searchRegex={searchRegex}
                                    infoOnlyWhenTruncated={!host}
                                    onInfoClick={() => host && onHostOpen(host)}
                                />
                            </Box>
                            <Typography variant="body2" className={classes.printOnlyTitle}>
                                {booking.meetingName}
                            </Typography>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" spacing=".5rem" useFlexGap>
                                {host && (
                                    <Typography variant="caption" color="text.secondary">
                                        <SearchableText search={searchRegex}>
                                            {[host.name, host.department?.name].filter(Boolean).join(" · ")}
                                        </SearchableText>
                                    </Typography>
                                )}
                                <MeetingRoomBookingTags booking={booking} room={room} />
                            </Stack>
                        </Stack>
                    )
                },
            }),
            columnFactory.nameColumn({
                id: "time",
                header: l10n.getString("service-center-meeting-rooms-table-time-header"),
                accessorFn: (row) => row.booking?.startDateTime ?? "",
                Cell: ({ row }) => <MeetingRoomTimeCell row={row.original} dayEndMillis={dayEndMillis} />,
            }),
            {
                ...columnFactory.statusColumn({
                    id: "status",
                    header: l10n.getString("service-center-meeting-rooms-table-status-header"),
                    accessorFn: (row) => row.status,
                    Cell: ({ row }) => {
                        const { liveBooking } = row.original
                        const chip = (
                            <StandardChip placement="table" variant={STATUS_CHIP_VARIANTS[row.original.status]}>
                                <Stack direction="row" alignItems="center" spacing=".5rem" component="span">
                                    {row.original.isInUseNow && <span className={classes.liveDot} />}
                                    <LocalizedEnum
                                        base="service-center-meeting-rooms-status"
                                        enumValue={row.original.status}
                                        vars={{ hour: hourRangeLabel }}
                                    >
                                        <span>{row.original.status}</span>
                                    </LocalizedEnum>
                                </Stack>
                            </StandardChip>
                        )
                        if (!liveBooking?.timeRange) return chip

                        return (
                            <LocalizedStrict
                                id="service-center-meeting-rooms-status-live-tooltip"
                                vars={{
                                    meeting: liveBooking.meetingName ?? "",
                                    time: formatTimeWindow(dateTimeFactory, liveBooking.timeRange),
                                }}
                                attrs={{ title: true }}
                            >
                                <Tooltip title="">
                                    <span>{chip}</span>
                                </Tooltip>
                            </LocalizedStrict>
                        )
                    },
                }),
                size: STATUS_COLUMN_SIZE,
                minSize: STATUS_COLUMN_SIZE,
                maxSize: STATUS_COLUMN_SIZE,
                muiTableHeadCellProps: { align: "right" },
                muiTableBodyCellProps: { align: "right" },
            },
        ],
        [
            l10n,
            dateTimeFactory,
            columnFactory,
            classes.liveDot,
            classes.roomName,
            classes.printHidden,
            classes.printOnlyTitle,
            searchRegex,
            onHostOpen,
            dayEndMillis,
            hourRangeLabel,
        ],
    )

    const isNoMatch = hasRooms && !rows.length && !isInitialLoading && !isError

    return (
        <>
            <ApiQueryTable
                isLoading={isInitialLoading}
                isFetching={isFetching}
                isError={isError}
                data={tableData}
                columns={columns}
                initialState={{ density: "spacious" }}
                // Rows are filtered by the page search; MRT only reuses it to highlight matches.
                manualFiltering
                state={{ globalFilter: search }}
                enableTopToolbar={false}
                enableColumnActions={false}
                enableColumnOrdering={false}
                enableGrouping={false}
                enableSorting={false}
                enablePagination={false}
                enableBottomToolbar={false}
                enableStickyHeader={false}
                renderRowActionMenuItems={({ row, closeMenu }) => {
                    const { booking, room } = row.original
                    const viewDetailsItem = (
                        <LocalizedStrict key="view-details" id="service-center-meeting-rooms-table-action-view-details">
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                    onRowOpen(row.original)
                                }}
                            >
                                View details
                            </MenuItem>
                        </LocalizedStrict>
                    )

                    if (booking) {
                        return [
                            <QueryErrorBoundary key="booking" Fallback={renderNoBookingActions}>
                                <MeetingCardMenu
                                    ignoreMenuContainer
                                    menuOnApi
                                    meeting={toMenuMeeting(booking)}
                                    handleMenuClose={closeMenu}
                                />
                            </QueryErrorBoundary>,
                            viewDetailsItem,
                        ]
                    }

                    if (!row.original.isBookable || !onBook) {
                        return [viewDetailsItem]
                    }

                    return [
                        viewDetailsItem,
                        <LocalizedStrict
                            key="book"
                            id="service-center-meeting-rooms-table-action-book"
                            vars={{ hour: hourLabel }}
                        >
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                    onBook(room)
                                }}
                            >
                                Book {hourLabel}
                            </MenuItem>
                        </LocalizedStrict>,
                    ]
                }}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        ...defaultDisplayColumnDefOptions["mrt-row-actions"],
                        muiTableHeadCellProps: { className: classes.actionsHeadCell },
                        muiTableBodyCellProps: { align: "center", className: classes.printHidden },
                    },
                }}
                muiTablePaperProps={{ className: classes.paper }}
                muiTableContainerProps={{
                    className: cx(
                        classes.tableContainer,
                        isInitialLoading && classes.tableContainerLoading,
                        !isInitialLoading && classes.tableContainerPaged,
                        isNoMatch && classes.tableContainerNoMatch,
                    ),
                }}
                muiTableBodyRowProps={({ row }) => ({
                    className: classes.row,
                    onClick: (event) => {
                        // Clicks in the portaled row menu and its dialogs bubble here through React, and the row
                        // buttons (menu trigger, host info) have their own action; only a plain row click opens the room drawer.
                        const isPlainRowClick =
                            event.target instanceof Element &&
                            event.currentTarget.contains(event.target) &&
                            !event.target.closest("button")
                        if (isPlainRowClick) onRowOpen(row.original)
                    },
                })}
            />
            {isNoMatch && (
                <LocalizedStrict id="service-center-meeting-rooms-table-no-match">
                    <Typography variant="body1" color="text.secondary" className={classes.noMatch}>
                        No rooms match the filters — reset status, floor or search.
                    </Typography>
                </LocalizedStrict>
            )}
        </>
    )
}
